<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import HeadersEditor from './HeadersEditor.vue';
import CollectVariablesEditor from './CollectVariablesEditor.vue';

const props = defineProps({
  rule: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const toHeaderRows = headers =>
  Object.entries(headers || {}).map(([name, value]) => ({ name, value }));

// Report options that ride on any aggregate shape (AiBot::ApiRule::AGGREGATE_OPTION_KEYS).
const REPORT_OPTION_KEYS = [
  'poll_seconds',
  'pending_template',
  'empty_template',
  'transfer_summary',
];
const PERIOD_PRESETS = [
  'today',
  'yesterday',
  'two_days',
  'this_week',
  'last_week',
  'this_month',
  'last_7_days',
];
const ruleAggregate = props.rule?.aggregate || {};

const draft = reactive({
  name: props.rule?.name || '',
  keywords: props.rule?.keywords || '',
  intent: props.rule?.intent || '',
  http_method: props.rule?.http_method || 'GET',
  url: props.rule?.url || '',
  headers: toHeaderRows(props.rule?.headers),
  body_template: props.rule?.body_template || '',
  auth_type: props.rule?.auth_type || 'none',
  auth_header_name: props.rule?.auth_header_name || 'Authorization',
  auth_secret: '',
  collect_variables: (props.rule?.collect_variables || []).map(variable => ({
    ...variable,
  })),
  aggregate_path: props.rule?.aggregate?.path || '',
  aggregate_op: props.rule?.aggregate?.op || 'sum',
  aggregate_field: props.rule?.aggregate?.field || '',
  aggregate_separator: props.rule?.aggregate?.separator || '',
  response_template: props.rule?.response_template || '',
  error_template: props.rule?.error_template || '',
  use_ai_response: props.rule?.use_ai_response || false,
  date_window: props.rule?.date_window || 'none',
  poll_seconds: ruleAggregate.poll_seconds ?? 0,
  pending_template: ruleAggregate.pending_template || '',
  empty_template: ruleAggregate.empty_template || '',
  transfer_summary: !!ruleAggregate.transfer_summary,
  transfer_summary_topics: (ruleAggregate.transfer_summary?.topics || []).join(
    ', '
  ),
  timeout_seconds: props.rule?.timeout_seconds ?? 10,
  priority: props.rule?.priority ?? 0,
  enabled: props.rule?.enabled ?? true,
  copilot_allowed: props.rule?.copilot_allowed || false,
});

const methodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => ({
  value: method,
  label: method,
}));

const authTypeOptions = [
  { value: 'none', label: t('AI_AGENT.API_RULES.FORM.AUTH_TYPES.NONE') },
  { value: 'bearer', label: t('AI_AGENT.API_RULES.FORM.AUTH_TYPES.BEARER') },
  { value: 'header', label: t('AI_AGENT.API_RULES.FORM.AUTH_TYPES.HEADER') },
  {
    value: 'md5_signature',
    label: t('AI_AGENT.API_RULES.FORM.AUTH_TYPES.MD5_SIGNATURE'),
  },
];

const aggregateOpOptions = ['sum', 'count', 'first', 'last', 'join'].map(
  op => ({
    value: op,
    label: op,
  })
);

const dateWindowOptions = [
  {
    value: 'none',
    label: t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.OPTIONS.NONE'),
  },
  {
    value: 'daily',
    label: t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.OPTIONS.DAILY'),
  },
  {
    value: 'weekly',
    label: t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.OPTIONS.WEEKLY'),
  },
  {
    value: 'monthly',
    label: t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.OPTIONS.MONTHLY'),
  },
  ...['message', 'fixed'].flatMap(kind =>
    PERIOD_PRESETS.map(preset => ({
      value: `${kind}:${preset}`,
      label: t(
        `AI_AGENT.API_RULES.FORM.DATE_WINDOW.OPTIONS.${kind.toUpperCase()}`,
        {
          preset: t(
            `AI_AGENT.API_RULES.FORM.DATE_WINDOW.PRESETS.${preset.toUpperCase()}`
          ),
        }
      ),
    }))
  ),
];

const hasAggregate = computed(() => draft.aggregate_path !== '');

// Rules imported from AegisBot keep their { sum, count, active_field } aggregate;
// the editor only handles the path/op shape, so show it read-only and leave it be.
const legacyAggregateKeys = computed(() => {
  const aggregate = props.rule?.aggregate;
  if (!aggregate || aggregate.op) return null;
  const legacy = Object.fromEntries(
    Object.entries(aggregate).filter(
      ([key]) => !REPORT_OPTION_KEYS.includes(key)
    )
  );
  return Object.keys(legacy).length ? legacy : null;
});

const legacyAggregate = computed(() =>
  legacyAggregateKeys.value ? JSON.stringify(legacyAggregateKeys.value) : ''
);

const reportOptions = () => {
  const options = {};
  const poll = Number(draft.poll_seconds) || 0;
  if (poll > 0) options.poll_seconds = Math.min(poll, 60);
  if (draft.pending_template) options.pending_template = draft.pending_template;
  if (draft.empty_template) options.empty_template = draft.empty_template;
  if (draft.transfer_summary) {
    const topics = draft.transfer_summary_topics
      .split(',')
      .map(topic => topic.trim())
      .filter(Boolean);
    options.transfer_summary = topics.length ? { topics } : {};
  }
  return options;
};

const hadReportOptions = REPORT_OPTION_KEYS.some(
  key => ruleAggregate[key] !== undefined
);

const secretPlaceholder = computed(() =>
  props.rule?.has_auth_secret
    ? t('AI_AGENT.API_RULES.FORM.HAS_AUTH_SECRET')
    : t('AI_AGENT.API_RULES.FORM.AUTH_SECRET_PLACEHOLDER')
);

const buildPayload = () => {
  const payload = {
    name: draft.name,
    keywords: draft.keywords || null,
    intent: draft.intent || null,
    http_method: draft.http_method,
    url: draft.url,
    headers: draft.headers
      .filter(header => header.name.trim() !== '')
      .reduce((acc, header) => {
        acc[header.name.trim()] = header.value;
        return acc;
      }, {}),
    body_template: draft.body_template || null,
    auth_type: draft.auth_type,
    auth_header_name:
      draft.auth_type === 'header' ? draft.auth_header_name : null,
    response_template: draft.response_template || null,
    error_template: draft.error_template || null,
    use_ai_response: draft.use_ai_response,
    date_window: draft.date_window,
    collect_variables: draft.collect_variables.filter(
      variable => variable.field.trim() !== ''
    ),
    timeout_seconds: Number(draft.timeout_seconds) || 10,
    priority: Number(draft.priority) || 0,
    enabled: draft.enabled,
    // Copilot may only run read-only (GET) rules.
    copilot_allowed: draft.http_method === 'GET' && draft.copilot_allowed,
  };
  if (draft.auth_secret !== '') {
    payload.auth_secret = draft.auth_secret;
  }
  const options = reportOptions();
  const hasOptions = Object.keys(options).length > 0;
  if (hasAggregate.value) {
    payload.aggregate = {
      path: draft.aggregate_path,
      op: draft.aggregate_op,
      field: draft.aggregate_op === 'count' ? null : draft.aggregate_field,
      separator: draft.aggregate_separator || null,
      ...options,
    };
  } else if (legacyAggregateKeys.value && (hasOptions || hadReportOptions)) {
    payload.aggregate = { ...legacyAggregateKeys.value, ...options };
  } else if (hasOptions) {
    payload.aggregate = options;
  } else if (hadReportOptions && !legacyAggregateKeys.value) {
    payload.aggregate = null;
  }
  return payload;
};

const submit = () => {
  emit('save', buildPayload());
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="submit">
    <Input
      v-model="draft.name"
      required
      :label="$t('AI_AGENT.API_RULES.FORM.NAME')"
      :placeholder="$t('AI_AGENT.API_RULES.FORM.NAME_PLACEHOLDER')"
      data-testid="rule-name"
    />
    <Input
      v-model="draft.keywords"
      :label="$t('AI_AGENT.API_RULES.FORM.KEYWORDS')"
      :placeholder="$t('AI_AGENT.API_RULES.FORM.KEYWORDS_PLACEHOLDER')"
      :message="$t('AI_AGENT.API_RULES.FORM.KEYWORDS_HELP')"
      data-testid="rule-keywords"
    />
    <Input
      v-model="draft.intent"
      :label="$t('AI_AGENT.API_RULES.FORM.INTENT')"
      :placeholder="$t('AI_AGENT.API_RULES.FORM.INTENT_PLACEHOLDER')"
      :message="$t('AI_AGENT.API_RULES.FORM.INTENT_HELP')"
      data-testid="rule-intent"
    />

    <div class="flex gap-2">
      <div class="w-36">
        <Select
          v-model="draft.http_method"
          :options="methodOptions"
          :label="$t('AI_AGENT.API_RULES.FORM.METHOD')"
          data-testid="rule-method"
        />
      </div>
      <div class="flex-1">
        <Input
          v-model="draft.url"
          required
          :label="$t('AI_AGENT.API_RULES.FORM.URL')"
          placeholder="https://api.example.com/v1/lookup"
          data-testid="rule-url"
        />
      </div>
    </div>

    <HeadersEditor v-model="draft.headers" />

    <TextArea
      v-model="draft.body_template"
      :label="$t('AI_AGENT.API_RULES.FORM.BODY_TEMPLATE')"
      :placeholder="$t('AI_AGENT.API_RULES.FORM.BODY_TEMPLATE_PLACEHOLDER')"
      :message="$t('AI_AGENT.API_RULES.FORM.TEMPLATE_HELP')"
      data-testid="rule-body-template"
    />

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.API_RULES.FORM.AUTH.LABEL') }}
      </span>
      <Select
        v-model="draft.auth_type"
        :options="authTypeOptions"
        data-testid="rule-auth-type"
      />
      <Input
        v-if="draft.auth_type === 'header'"
        v-model="draft.auth_header_name"
        :label="$t('AI_AGENT.API_RULES.FORM.AUTH_HEADER_NAME')"
        data-testid="rule-auth-header-name"
      />
      <Input
        v-if="draft.auth_type !== 'none'"
        v-model="draft.auth_secret"
        type="password"
        :label="$t('AI_AGENT.API_RULES.FORM.AUTH_SECRET')"
        :placeholder="secretPlaceholder"
        data-testid="rule-auth-secret"
      />
    </div>

    <CollectVariablesEditor v-model="draft.collect_variables" />

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.LABEL') }}
      </span>
      <Select
        v-model="draft.date_window"
        :options="dateWindowOptions"
        :aria-label="$t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.LABEL')"
        data-testid="rule-date-window"
      />
      <p class="text-xs text-n-slate-10">
        {{ $t('AI_AGENT.API_RULES.FORM.DATE_WINDOW.HELP') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.API_RULES.FORM.AGGREGATE.LABEL') }}
      </span>
      <p class="text-xs text-n-slate-10">
        {{ $t('AI_AGENT.API_RULES.FORM.AGGREGATE.HELP') }}
      </p>
      <p
        v-if="legacyAggregate"
        class="text-xs text-n-slate-11"
        data-testid="rule-legacy-aggregate"
      >
        {{ $t('AI_AGENT.API_RULES.FORM.AGGREGATE.LEGACY') }}
        <code>{{ legacyAggregate }}</code>
      </p>
      <div v-else class="flex gap-2">
        <Input
          v-model="draft.aggregate_path"
          :label="$t('AI_AGENT.API_RULES.FORM.AGGREGATE.PATH')"
          placeholder="data.items"
          class="flex-1"
          data-testid="rule-aggregate-path"
        />
        <div class="w-28">
          <Select
            v-model="draft.aggregate_op"
            :options="aggregateOpOptions"
            :label="$t('AI_AGENT.API_RULES.FORM.AGGREGATE.OP')"
          />
        </div>
        <Input
          v-if="draft.aggregate_op !== 'count'"
          v-model="draft.aggregate_field"
          :label="$t('AI_AGENT.API_RULES.FORM.AGGREGATE.FIELD')"
          class="flex-1"
        />
        <Input
          v-if="draft.aggregate_op === 'join'"
          v-model="draft.aggregate_separator"
          :label="$t('AI_AGENT.API_RULES.FORM.AGGREGATE.SEPARATOR')"
          class="w-24"
        />
      </div>
    </div>

    <div class="flex flex-col gap-2" data-testid="rule-report-options">
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.API_RULES.FORM.REPORT.LABEL') }}
      </span>
      <p class="text-xs text-n-slate-10">
        {{ $t('AI_AGENT.API_RULES.FORM.REPORT.HELP') }}
      </p>
      <Input
        v-model="draft.poll_seconds"
        type="number"
        min="0"
        max="60"
        :label="$t('AI_AGENT.API_RULES.FORM.REPORT.POLL_SECONDS')"
        :message="$t('AI_AGENT.API_RULES.FORM.REPORT.POLL_SECONDS_HELP')"
        data-testid="rule-poll-seconds"
      />
      <TextArea
        v-model="draft.pending_template"
        :label="$t('AI_AGENT.API_RULES.FORM.REPORT.PENDING_TEMPLATE')"
        data-testid="rule-pending-template"
      />
      <TextArea
        v-model="draft.empty_template"
        :label="$t('AI_AGENT.API_RULES.FORM.REPORT.EMPTY_TEMPLATE')"
        :message="$t('AI_AGENT.API_RULES.FORM.REPORT.EMPTY_TEMPLATE_HELP')"
        data-testid="rule-empty-template"
      />
      <div class="flex items-start gap-2">
        <Switch
          v-model="draft.transfer_summary"
          data-testid="rule-transfer-summary"
        />
        <div class="flex flex-col">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_AGENT.API_RULES.FORM.REPORT.TRANSFER_SUMMARY') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ $t('AI_AGENT.API_RULES.FORM.REPORT.TRANSFER_SUMMARY_HELP') }}
          </span>
        </div>
      </div>
      <Input
        v-if="draft.transfer_summary"
        v-model="draft.transfer_summary_topics"
        :label="$t('AI_AGENT.API_RULES.FORM.REPORT.TRANSFER_TOPICS')"
        placeholder="klaim_bonus, cek_turnover"
        data-testid="rule-transfer-topics"
      />
    </div>

    <TextArea
      v-model="draft.response_template"
      :label="$t('AI_AGENT.API_RULES.FORM.RESPONSE_TEMPLATE')"
      :placeholder="$t('AI_AGENT.API_RULES.FORM.RESPONSE_TEMPLATE_PLACEHOLDER')"
      :message="$t('AI_AGENT.API_RULES.FORM.TEMPLATE_HELP')"
      data-testid="rule-response-template"
    />
    <TextArea
      v-model="draft.error_template"
      :label="$t('AI_AGENT.API_RULES.FORM.ERROR_TEMPLATE')"
      :placeholder="$t('AI_AGENT.API_RULES.FORM.ERROR_TEMPLATE_PLACEHOLDER')"
      data-testid="rule-error-template"
    />

    <div class="flex items-center gap-2">
      <Switch
        v-model="draft.use_ai_response"
        data-testid="rule-use-ai-response"
      />
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.API_RULES.FORM.USE_AI_RESPONSE') }}
      </span>
    </div>
    <div class="flex items-center gap-2">
      <Switch v-model="draft.enabled" data-testid="rule-enabled" />
      <span class="text-sm font-medium text-n-slate-12">
        {{ $t('AI_AGENT.API_RULES.FORM.ENABLED') }}
      </span>
    </div>
    <div v-if="draft.http_method === 'GET'" class="flex items-start gap-2">
      <Switch
        v-model="draft.copilot_allowed"
        data-testid="rule-copilot-allowed"
      />
      <div class="flex flex-col">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AI_AGENT.API_RULES.FORM.COPILOT_ALLOWED') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ $t('AI_AGENT.API_RULES.FORM.COPILOT_ALLOWED_HELP') }}
        </span>
      </div>
    </div>

    <div class="flex gap-2">
      <Input
        v-model="draft.timeout_seconds"
        type="number"
        min="1"
        max="30"
        :label="$t('AI_AGENT.API_RULES.FORM.TIMEOUT')"
        class="w-32"
        data-testid="rule-timeout"
      />
      <Input
        v-model="draft.priority"
        type="number"
        :label="$t('AI_AGENT.API_RULES.FORM.PRIORITY')"
        class="w-32"
        data-testid="rule-priority"
      />
    </div>

    <div class="flex items-center justify-end w-full gap-2">
      <Button
        faded
        slate
        type="button"
        :label="$t('AI_AGENT.API_RULES.FORM.CANCEL')"
        data-testid="rule-cancel"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        :label="$t('AI_AGENT.API_RULES.FORM.SUBMIT')"
        :is-loading="isSaving"
        data-testid="rule-submit"
      />
    </div>
  </form>
</template>
